import React, { useState, useEffect, useCallback } from "react";

// Tarih formatlama fonksiyonu
const formatDate = (dateString) => {
  // Gelen değerin geçerli bir tarih olup olmadığını kontrol et
  if (!dateString) {
    return "Tarih belirtilmemiş";
  }

  // Gelen değer bir sayısal timestamp ise (localStorage'dan gelebilir)
  // önce sayıya çevirip sonra Date nesnesine verelim.
  const dateValue = !isNaN(dateString) ? parseInt(dateString) : dateString;
  const date = new Date(dateValue);

  if (isNaN(date.getTime())) {
    return "Geçersiz Tarih";
  }

  return date.toLocaleString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SonTweetler = ({ apiEndpoint, username, profilePic }) => {
  const [tweets, setTweets] = useState({ data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);

  const fetchTweets = useCallback(async () => {
    // Önbellek kontrolü
    const cachedTweets = localStorage.getItem("tweets");
    const cachedTimestamp = localStorage.getItem("tweetsTimestamp");
    const cacheDuration = 24 * 60 * 60 * 1000; // 24 saat

    if (cachedTweets && cachedTimestamp && Date.now() - parseInt(cachedTimestamp) < cacheDuration) {
      setTweets(JSON.parse(cachedTweets));
      setLoading(false);
      return;
    }

    // API isteği
    setLoading(true);
    setError(null);
    setRetryAfter(0);

    try {
      const response = await fetch(apiEndpoint);
      const retryAfterHeader = response.headers.get("Retry-After");

      if (response.status === 429) {
        const data = await response.json();
        const retrySeconds = data.error?.retryAfter || (retryAfterHeader ? parseInt(retryAfterHeader) : 60);
        setError(data.error?.detail || "Çok fazla istek yapıldı. Lütfen bekleyin.");
        setRetryAfter(retrySeconds);
        if (cachedTweets) setTweets(JSON.parse(cachedTweets)); // Hata anında önbelleği göster
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Tweetler yüklenemedi.`);
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.data)) {
        throw new Error("API'den beklenen formatta veri alınamadı.");
      }

      setTweets(data);
      localStorage.setItem("tweets", JSON.stringify(data));
      localStorage.setItem("tweetsTimestamp", Date.now().toString());
    } catch (err) {
      console.error("Tweet çekme hatası:", err);
      setError(err.message || "Bilinmeyen bir hata oluştu.");
      if (cachedTweets) setTweets(JSON.parse(cachedTweets)); // Hata anında önbelleği göster
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint]);

  // İlk yüklemede tweetleri çek
  useEffect(() => {
    fetchTweets();
  }, [fetchTweets]);

  // Geri sayım sayacı
  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            fetchTweets(); // Süre dolunca tekrar dene
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfter, fetchTweets]);

  const renderTweetCards = (tweetData) => {
    if (!tweetData || !tweetData.data || tweetData.data.length === 0) {
      return (
        <div className="col-12 text-center">
          <p className="text-muted">Gösterilecek tweet bulunamadı.</p>
        </div>
      );
    }

    return tweetData.data.slice(0, 3).map((tweet, index) => (
      <div className="col-md-4" key={tweet.id || index} data-aos="fade-up" data-aos-delay={index * 100}>
        <div className="card tweet-card shadow-sm border-0 h-100">
          <div className="card-body p-3">
            <div className="d-flex align-items-center mb-3">
              <img
                src={profilePic}
                alt={`${username} Profil Resmi`}
                className="rounded-circle me-2"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                loading="lazy"
              />
              <div>
                <h3 className="mb-0 fw-bold">Hacı Ali Kara</h3>
                <small className="text-muted">@{username}</small>
              </div>
            </div>
            <p className="card-text mb-2" style={{ lineHeight: "1.5" }}>
              {tweet.text}
            </p>
            <small className="text-muted">{formatDate(tweet.created_at)}</small>
          </div>
          <div className="card-footer bg-transparent border-0 py-2">
            <div className="d-flex justify-content-between text-muted small">
              <span><i className="bi bi-chat me-1"></i> Yorum</span>
              <span><i className="bi bi-arrow-repeat me-1"></i> Retweet</span>
              <span><i className="bi bi-heart me-1"></i> Beğeni</span>
            </div>
          </div>
        </div>
      </div>
    ));
  };
  
  return (
    <div className="container my-5">
      <h2 className="text-center mb-4" data-aos="fade-up">Son Tweetlerim</h2>
      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="mt-2">Tweet'ler yükleniyor...</p>
        </div>
      ) : (
        <>
          {error && (
            <div className="alert alert-warning text-center">
              <p className="mb-1 fw-bold">{error}</p>
              {retryAfter > 0 ? (
                <p className="mb-0 small">
                  Yeni deneme için kalan süre: {retryAfter} saniye
                </p>
              ) : (
                 <p className="mb-0 small">Önbellekteki son veriler gösteriliyor.</p>
              )}
            </div>
          )}
          <div className="row g-4">{renderTweetCards(tweets)}</div>
           {!retryAfter && !loading && (
             <div className="text-center mt-4">
                <button className="btn btn-primary px-4" onClick={fetchTweets}>
                  Tweet'leri Güncelle
                </button>
             </div>
           )}
        </>
      )}
    </div>
  );
};

export default SonTweetler;
